import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { AUTH_ROLES } from "api/common/types/auth.types";
import { Option } from "api/common/types/common.types";
import { useGetProductFeedbackQuery } from "api/memberPortal/product/feedback/feedback.api";
import { useGetProductQuery } from "api/memberPortal/project/product/product.api";
import { useAuth } from "hooks/useAuth";

import { OPTION_DELIMITER } from "../ProficiencyFeedbackForm.schema";

import ProficiencyFeedbackForm from "./ProficiencyFeedback.form";

/** Convert a pipe-delimited string from the API into Option[] for the form */
const stringToOptions = (value: string | undefined | null): Option[] => {
  if (!value) return [];
  return value
    .split(OPTION_DELIMITER)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((v) => ({ value: v, label: v }));
};

const ProficiencyFeedbackModal = () => {
  const { id } = useParams();
  const { permissions } = useAuth();
  const navigate = useNavigate();

  const { data: feedbackForm } = useGetProductFeedbackQuery(
    id
      ? {
          parentId: id,
        }
      : skipToken,
  );

  const readonly =
    feedbackForm?.status === "final" ||
    permissions.role_type === AUTH_ROLES.NIAP;

  const isNiap = permissions.role_type === AUTH_ROLES.NIAP;

  useEffect(() => {
    if (isNiap && feedbackForm?.status !== "final") {
      navigate(`products/details/${id}`);
    }
  }, [feedbackForm?.status, isNiap, navigate, id]);

  const { data: product } = useGetProductQuery(id ?? skipToken);

  // Convert flat API response into nested form structure
  const formDefaults = useMemo(() => {
    if (!feedbackForm) return undefined;

    const {
      validationTeamTwoWeeks,
      validationTeamDays,
      missTds,
      missTdsEcrs,
      misinterpretPolicy,
      misinterpretedPolicies,
      performTests,
      performTestsEcrs,
      ...rest
    } = feedbackForm;

    return {
      ...rest,
      validationTeam: { validationTeamTwoWeeks, validationTeamDays },
      missTdsGroup: {
        missTds,
        missTdsEcrs: stringToOptions(missTdsEcrs),
      },
      misinterpretPolicyGroup: {
        misinterpretPolicy,
        misinterpretedPolicies: stringToOptions(misinterpretedPolicies),
      },
      performTestsGroup: {
        performTests,
        performTestsEcrs: stringToOptions(performTestsEcrs),
      },
    };
  }, [feedbackForm]);

  if (!product) return;

  return (
    <ProficiencyFeedbackForm
      feedbackForm={formDefaults}
      product={product}
      readonly={readonly}
      pdfFileId={feedbackForm?.pdfFileId ?? null}
    />
  );
};
export default ProficiencyFeedbackModal;
